import { authorizedFetch, API_ORIGIN, useAuth } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

export const WATCHED_ORGANISATIONS_QUERY_KEY = ['watched-organisations'] as const

export type WatchedOrganisation = {
  slug: string
  name: string
  logo_url: string | null
  industry: string
  city: string
  country: string
}

export type WatchedOrganisationsResponse = {
  results: WatchedOrganisation[]
}

export function useWatchedOrganisations(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: WATCHED_ORGANISATIONS_QUERY_KEY,
    queryFn: async (): Promise<WatchedOrganisationsResponse> => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/profiles/followed-organisations/`,
      )
      if (!response.ok) {
        throw new Error('Failed to load watched companies')
      }
      return response.json()
    },
    enabled: enabled && Boolean(token),
  })

  const unwatchMutation = useMutation({
    mutationFn: async (slug: string) => {
      const trimmed = slug.trim()
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/organisations/${encodeURIComponent(trimmed)}/follow/`,
        { method: 'DELETE' },
      )
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(
          (error as { error?: string }).error || 'Could not unwatch company',
        )
      }
      return trimmed
    },
    onMutate: async (slug) => {
      const trimmed = slug.trim()
      await queryClient.cancelQueries({ queryKey: WATCHED_ORGANISATIONS_QUERY_KEY })
      const previous = queryClient.getQueryData<WatchedOrganisationsResponse>(
        WATCHED_ORGANISATIONS_QUERY_KEY,
      )
      if (previous) {
        queryClient.setQueryData<WatchedOrganisationsResponse>(
          WATCHED_ORGANISATIONS_QUERY_KEY,
          {
            results: previous.results.filter((row) => row.slug !== trimmed),
          },
        )
      }
      return { previous }
    },
    onError: (error, _slug, context) => {
      if (context?.previous) {
        queryClient.setQueryData(WATCHED_ORGANISATIONS_QUERY_KEY, context.previous)
      }
      const message = error instanceof Error ? error.message : 'Could not unwatch company'
      toast.error(message)
    },
    onSuccess: (slug) => {
      void queryClient.invalidateQueries({ queryKey: WATCHED_ORGANISATIONS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['organisation-follow', slug] })
    },
  })

  return {
    organisations: listQuery.data?.results ?? [],
    isLoading: listQuery.isLoading,
    error: listQuery.error instanceof Error ? listQuery.error.message : null,
    refetch: listQuery.refetch,
    unwatch: (slug: string) => unwatchMutation.mutate(slug),
    unwatchingSlug: unwatchMutation.isPending ? unwatchMutation.variables ?? null : null,
  }
}
